/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { Action, PreloadedState, ThunkAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';

import chatReducer from '../features/sharedcomponents/chat/chatSlice';
import commentsReducer from '../features/Comments/commentsSlice';
import feedbackReducer from '../features/sharedcomponents/chat/DocumentVersionFeedback/feedbackSlice';
import favoriteReducer from '../features/PersonalArea/favoriteSlice';
import cmeReducer from '../features/PersonalArea/cmeSlice';
import medicationReducer from '../features/sharedcomponents/chat/components/InnerMessage/InnerMessageExtra/Medication/medicationSlice';
import patientSummaryReducer from '../features/sharedcomponents/chat/components/PatientSummary/patientSummarySlice';
import fhirReducer from '../features/sharedcomponents/Fhir/fhirSlice';
import listenerMiddleware from '../features/sharedcomponents/listenerMiddleware';
import searchReducer from '../features/sharedcomponents/chat/searchSlice';
import uiReducer from '../features/sharedcomponents/ui/uiSlice';
import userReducer from '../features/sharedcomponents/Login/userSlice';
import medPearlOneFeedbackReducer from '../features/sharedcomponents/chat/DocumentVersionFeedback/medPearlOneFeedbackSlice';
import npiReducer from '../features/NPI/npiSlice';
import userEnrollmentReducer from '../features/AffiliateAdmin/UserEnrollmentSlice';

const sentryEnhancer = createReduxEnhancer();

/** Create the root reducer separately so we can extract the RootState type */
const rootReducer = combineReducers({
  chat: chatReducer,
  comments: commentsReducer,
  search: searchReducer,
  ui: uiReducer,
  user: userReducer,
  fhir: fhirReducer,
  feedback: feedbackReducer,
  favorite: favoriteReducer,
  cme: cmeReducer,
  medication: medicationReducer,
  patientSummary: patientSummaryReducer,
  medPearlOneFeedback: medPearlOneFeedbackReducer,
  npi: npiReducer,
  userEnrollment: userEnrollmentReducer,
});

export const setupStore = (preloadedState?: PreloadedState<RootState>, isUnitTests: boolean = false) => {
  const middlewareConfig = isUnitTests ? { serializableCheck: false, immutableCheck: false } : {};
  return configureStore({
    reducer: rootReducer,
    // Add the listener middleware to the store.
    // NOTE: Since this can receive actions with functions inside,
    // it should go before the serializability check middleware
    middleware: (getDefaultMiddleware) => getDefaultMiddleware(middlewareConfig).prepend(listenerMiddleware.middleware),
    enhancers: [sentryEnhancer],
    devTools: process.env.NODE_ENV !== 'production',
    preloadedState,
  });
};

declare global {
  interface Window {
    store: ReturnType<typeof setupStore>;
  }
}

export const store = setupStore();
window.store = store;

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
